import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/helpers';
import './TopMarketsSection.css';

// Volume filter options (values only, labels are generated with translation)
const VOLUME_VALUES = [10000, 50000, 100000, 500000];

function TopMarketsSection({ markets, loading, onMarketClick }) {
  const { t } = useTranslation();
  const [minVolume, setMinVolume] = useState(10000);

  // Filter and sort markets
  const filteredMarkets = useMemo(() => {
    if (!markets || markets.length === 0) return [];

    return [...markets]
      .filter(market => {
        // Volume filter
        if (market.totalVolume < minVolume) return false;
        return true;
      })
      .sort((a, b) => b.totalVolume - a.totalVolume)
      .slice(0, 10);
  }, [markets, minVolume]);

  if (loading) {
    return (
      <div className="card top-markets-card">
        <div className="top-markets-header">
          <h3 className="top-markets-title">🔥 {t('polymarket.topMarkets')}</h3>
        </div>
        <div className="top-markets-body">
          <div className="loading-state">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card top-markets-card">
      {/* Header */}
      <div className="top-markets-header">
        <h3 className="top-markets-title">🔥 {t('polymarket.topMarkets')}</h3>
        <select
          className="polymarket-filter-dropdown compact"
          value={minVolume}
          onChange={(e) => setMinVolume(Number(e.target.value))}
        >
          {VOLUME_VALUES.map(value => (
            <option key={value} value={value}>
              Vol &gt; ${value >= 1000 ? `${value / 1000}k` : value}
            </option>
          ))}
        </select>
      </div>

      {/* Column Headers */}
      <div className="top-markets-columns">
        <div className="col-market">{t('polymarket.market')}</div>
        <div className="col-volume">{t('polymarket.volume')}</div>
      </div>

      {/* Markets List */}
      <div className="top-markets-body">
        {filteredMarkets.length === 0 ? (
          <div className="no-markets">
            <p>{t('polymarket.noMarketsYet')}</p>
          </div>
        ) : (
          filteredMarkets.map((market, index) => (
            <MarketRow
              key={market.marketSlug}
              market={market}
              rank={index + 1}
              isHot={index < 3}
              onClick={() => onMarketClick && onMarketClick(market)}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Split title into array of lines (~25 chars each to match visual width)
const splitTitleIntoLines = (title, charsPerLine = 25) => {
  if (!title) return [''];
  if (title.length <= charsPerLine) return [title];

  const lines = [];
  let remaining = title;

  while (remaining.length > 0) {
    if (remaining.length <= charsPerLine) {
      lines.push(remaining);
      break;
    }
    const cutIndex = remaining.lastIndexOf(' ', charsPerLine);
    const splitAt = cutIndex > 10 ? cutIndex : charsPerLine;
    lines.push(remaining.substring(0, splitAt));
    remaining = remaining.substring(splitAt).trim();
  }

  return lines;
};

// Market Row component
function MarketRow({ market, rank, isHot, onClick, t }) {
  // Split title into lines for display
  const titleLines = splitTitleIntoLines(market.marketTitle || market.marketSlug);
  const needsAnimation = titleLines.length > 2;

  return (
    <div
      className={`top-market-row ${isHot ? 'hot' : ''}`}
      onClick={onClick}
    >
      {/* Rank/Hot Badge */}
      <div className="market-rank-badge">
        {isHot ? (
          <span className="hot-badge">{t('polymarket.hot')}</span>
        ) : (
          <span className="rank-number">#{rank}</span>
        )}
      </div>

      {/* Market Info + Probability Bar */}
      <div className="market-info-col">
        <div className="market-header-row">
          {market.marketIcon && (
            <img
              className="market-icon"
              src={market.marketIcon}
              alt=""
            />
          )}
          <div className="market-title-container" title={market.marketTitle || market.marketSlug}>
            {needsAnimation ? (
              <div className={`market-title-scroller scroller-lines-${titleLines.length}`}>
                {titleLines.map((line, i) => (
                  <span key={i} className="market-title-line">{line}</span>
                ))}
              </div>
            ) : (
              titleLines.map((line, i) => (
                <span key={i} className="market-title-line">{line}</span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Volume */}
      <div className="volume-col">
        <span className="volume-amount">${formatNumber(market.totalVolume)}</span>
        <span className="trade-count">{market.tradeCount} {t('polymarket.trades')}</span>
      </div>
    </div>
  );
}

export default TopMarketsSection;
